import { ENDPOINT_REQUEST } from "@/configurations";
import { ACTION_TYPE } from "@/constants/w-socket";
import { getRequestTimeout, WebSocketWrapper } from "./websocket.helper";
import { createMDSLoginParams, handleMDSLogin } from "@/guards/mds";
import { onWebSocketClose, onWebSocketOpen } from "./websocket.state";
import { getInitState } from "@/helpers/global";

const initStateFunc = getInitState({
	EIFLogin: true,
});
const LOGIN_ITEM = initStateFunc("TTLLogin");

const count = Object.defineProperty({ number: 20 }, "key", {
	get() {
		if (this.number > 32767) {
			this.number = 20;
		}
		return this.number++;
	},
});

// url 为 "ws://ttl2aacloud01.tx-tech.com/streaming" 等链家地址
// data 为 { symbols: [{ exchangeID: "HK", key: "1", subscribe: true, msgTypes: ["NOMINAL_PRICE"] }
// onMessage，onError 为回调业务方法
// sign 为 { url, data, onMessage, onError }
// reqKey 为 reqId(例：8,9,10...) 或 msgType + market type + stock id(例：NOMINAL_PRICE_HK_1)

let connected = {}; // 连接实例 { [url]: WebSocketWrapper }
let requestMap = {}; // 请求字典 { [url]: { reqKey: sign[] } }
let loginSessionID; // 会话ID
let retryLoginRequestState = false; // 重发登录请求状态

const serialization = (data) => (data && typeof data === "object" ? JSON.stringify(data) : data);

const hasPubType = (url) => url === ENDPOINT_REQUEST.wsSTOCK.STREAMING || url === ENDPOINT_REQUEST.wsSTOCK.MDS9997;

// 获取连接实例
function getWebSocket(url) {
	if (!connected[url]) {
		connected[url] = new WebSocketWrapper(url, handleMessage, handleOpen, handleClose, handleReconnect);
	}
	return connected[url];
}

// 发送请求
function handleSendMessage(ws, data) {
	if (!ws) return;

	if (ws.webSocketState === "OPEN" && ws.webSocket.readyState === WebSocket.OPEN) {
		if (data.msgType === ACTION_TYPE.LOGIN) {
			ws.send(serialization(data));
			return;
		} else if (loginSessionID) {
			data.sessID = loginSessionID;
			ws.send(serialization(data));
			return;
		}
	}
}

// 处理响应
function handleMessage(ws, data) {
	if (!ws) return;

	const isPush = hasPubType(ws.url);

	let reqKey;
	let deserialization;
	try {
		deserialization = JSON.parse(data);
	} catch (e) {
		deserialization = data;
	}

	if (isPush) {
		reqKey = getResKey(deserialization);
	} else {
		reqKey = deserialization.reqId;
	}

	const dictionary = requestMap?.[ws.url]?.[reqKey];
	if (Array.isArray(dictionary)) {
		dictionary.forEach((v) => {
			v.clearReqTimeout();
			v.onMessage(deserialization);

			if (!Object.hasOwn(deserialization, "rtnCode") || ![408, 401].includes(deserialization.rtnCode)) {
				if (!isPush) {
					if (Array.isArray(v.ready)) {
						const isEveryIsTrue = v.ready.every((c) => {
							const resKey = getResKey(deserialization);
							const cMsgType = getMsgTypeKey(v.data);
							const cReqKey = [cMsgType, c.exchangeID, c.key].join("_");
							if (cReqKey === resKey) {
								c.request = true;
							}
							return c.request;
						});
						if (isEveryIsTrue) {
							delete requestMap[ws.url][reqKey];
						}
					} else {
						delete requestMap[ws.url][reqKey];
					}
				}
			}
		});
	}

	if (deserialization.rtnCode === 408) {
		ws.close();
		ws.reconnect();
		return;
	}

	// if (deserialization.rtnCode === 401) {
	// 	loginSessionID = null;
	// 	handleRetryLogin();
	// 	return;
	// }

	if (deserialization.msgType === ACTION_TYPE.LOGIN) {
		retryLoginRequestState = false;
		if (deserialization.rtnCode === 0) {
			loginSessionID = deserialization.sessionID;
			// 发送订阅字典
			Object.keys(requestMap).forEach((url) => {
				const itemWs = getWebSocket(url);
				if (itemWs.webSocketState === "OPEN" && itemWs.webSocket.readyState === WebSocket.OPEN) {
					handleOpenSendMessage(itemWs);
				}
			});
		} else {
			handleRetryLogin();
		}
	}

	if (deserialization.msgType === ACTION_TYPE.LOGOUT) {
		Object.values(connected).forEach((server) => server.close());
		connected = {};
		requestMap = {};
		loginSessionID = null;
		retryLoginRequestState = false;
	}
}

// 连接成功事件
function handleOpen(ws, event) {
	if (!ws) return;
	handleOpenSendMessage(ws);
	onWebSocketOpen(ws, event);
}

// 连接关闭事件
function handleClose(ws, event) {
	if (!ws) return;
	onWebSocketClose(ws, event);
}

// 连接重试事件
function handleReconnect(ws) {
	if (!ws) return;
}

// 连接成功发送请求
function handleOpenSendMessage(ws) {
	if (!ws) return;

	if (requestMap[ws.url]) {
		if (LOGIN_ITEM && ws.url === ENDPOINT_REQUEST.wsSTOCK.SNAPSHOT || !LOGIN_ITEM && ws.url === ENDPOINT_REQUEST.wsSTOCK.OTHERS || loginSessionID) {
			Object.values(requestMap[ws.url] || {}).forEach((list) => {
				if (hasPubType(ws.url) && loginSessionID) {
					handleSendMessage(ws, list?.[0]?.data);
				} else {
					list.forEach(({ data }) => handleSendMessage(ws, data));
				}
			});
		}
	}
}

// 重试登录请求
function handleRetryLogin() {
	if (!retryLoginRequestState) {
		retryLoginRequestState = true;

		const url = LOGIN_ITEM ? ENDPOINT_REQUEST.wsSTOCK.SNAPSHOT : ENDPOINT_REQUEST.wsSTOCK.OTHERS;
		const data = createMDSLoginParams();
		const onMessage = handleMDSLogin;
		const clearReqTimeout = getRequestTimeout(onMessage);
		const sign = { url, data, onMessage, onError: () => { }, clearReqTimeout };
		const reqKey = data.reqId;
		pushRequestMap(url, reqKey, sign);
		handleSendMessage(getWebSocket(url), data);
	}
}

// 处理业务请求
export function openConnection(url, data, onMessage = () => { }, onError = () => { }) {
	const ws = getWebSocket(url);
	const isPush = hasPubType(url);

	let reqKey;
	let unsubscribe = () => { };

	if (!ws) return unsubscribe;

	const clearReqTimeout = getRequestTimeout(onMessage);

	const sign = { url, data, onMessage, onError, clearReqTimeout };

	if (isPush) {
		const msgType = getMsgTypeKey(data);
		if (data.symbols) {
			reqKey = [msgType, data.symbols?.[0]?.exchangeID, data.symbols?.[0]?.key].join("_");
		} else if (data.symbol) {
			reqKey = [msgType, data.symbol?.exchangeID, data.symbol?.key].join("_");
		}
		unsubscribe = () => {
			if (Array.isArray(requestMap?.[url]?.[reqKey])) {
				if (requestMap[url][reqKey].length >= 2) {
					requestMap[url][reqKey] = requestMap[url][reqKey].filter((v) => v !== sign);
				} else {
					handleSendMessage(getWebSocket(url), { ...data, subscribe: false });
					delete requestMap[url][reqKey];
				}
			}
		};
	} else {
		// snapshot 和 info，则以 reqId 作为 reqKey
		reqKey = data.reqId || count.key;
		data.reqId = reqKey;

		if (Array.isArray(data.symbols)) {
			sign.ready = data.symbols.map((item) => ({ exchangeID: item.exchangeID, key: item.key, request: false }));
		}

		unsubscribe = () => {
			if (requestMap?.[url]?.[reqKey]) {
				delete requestMap[url][reqKey];
			}
		};
	}

	pushRequestMap(url, reqKey, sign);

	handleSendMessage(ws, data);

	return unsubscribe;
}

function getResKey(data) {
	return [data.msgType, data.exchangeID, data.code || data.securityCode].join("_");
}

function getMsgTypeKey(data) {
	return Array.isArray(data.msgTypes) ? data.msgTypes.toString() : data.msgType;
}

function pushRequestMap(url, reqKey, sign) {
	if (!requestMap[url]) {
		requestMap[url] = {};
	}

	if (!requestMap[url][reqKey]) {
		requestMap[url][reqKey] = [];
	}

	requestMap[url][reqKey].push(sign);
}
